import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  XMarkIcon,
  ClockIcon,
  MapPinIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  UserIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import {
  PaperAirplaneIcon,
  EyeIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/solid";
import { formatForDisplay } from "../utils/gmtUtils";
import { getAuditLogs } from "../api/signRequests";
import AnimatedModal from "./ui/AnimatedModal";

const hasDetails = () => true; // All logs have at least datetime; expandable for all

const DocumentAuditTrail = ({ isOpen, onClose, signRequestId, documentName, recipients = [] }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState(new Set());

  useEffect(() => {
    if (isOpen && signRequestId) {
      fetchAuditLogs();
      setExpandedLogs(new Set());
    }
  }, [isOpen, signRequestId]);

  // Expand all by default when audit logs load
  useEffect(() => {
    if (auditLogs.length > 0) {
      const allIds = auditLogs.map((log, index) => log._id ?? index);
      setExpandedLogs(new Set(allIds));
    }
  }, [auditLogs]);

  const toggleLogExpanded = (logId) => {
    setExpandedLogs((prev) => {
      const next = new Set(prev);
      if (next.has(logId)) next.delete(logId);
      else next.add(logId);
      return next;
    });
  };

  const allLogIds = auditLogs.map((log, index) => log._id ?? index);
  const allExpanded = allLogIds.length > 0 && allLogIds.every((id) => expandedLogs.has(id));
  const expandCollapseAll = () => {
    if (allExpanded) {
      setExpandedLogs(new Set());
    } else {
      setExpandedLogs(new Set(allLogIds));
    }
  };

  const fetchAuditLogs = async () => {
    try {
      setIsLoading(true);
      const response = await getAuditLogs(signRequestId);
      // Filter out DOCUMENT_OPENED events from old email addresses
      const filteredLogs = (response.data || []).filter((log) => {
        // If it's a DOCUMENT_OPENED event, check if the email matches current recipient email
        if (log.action === "DOCUMENT_OPENED" && log.contactId) {
          const logEmail = log.contactId?.email;
          const logContactId = log.contactId?._id || log.contactId?.id;
          
          // Find the recipient with matching contactId
          const recipient = recipients?.find(
            (r) => r.contactId === logContactId || r._id === logContactId
          );
          
          // Only include if recipient exists and email matches current recipient email
          if (recipient && recipient.email && logEmail) {
            return logEmail.toLowerCase() === recipient.email.toLowerCase();
          }
          // Exclude if recipient not found or email doesn't match (old email)
          return false;
        }
        // Include all other events
        return true;
      });
      setAuditLogs(filteredLogs);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getActionIcon = (action) => {
    const iconClass = "h-9 w-9 rounded-full flex items-center justify-center ring-2 ring-white shadow-sm";
    // Match exact action types from API
    if (action === "SENT_EMAIL" || action === "CREATED_SIGN_REQUEST") {
      return (
        <div className={`${iconClass} bg-primary`}>
          <PaperAirplaneIcon className="h-4 w-4 text-white" />
        </div>
      );
    } else if (action === "DOCUMENT_OPENED") {
      return (
        <div className={`${iconClass} bg-amber-500`}>
          <EyeIcon className="h-4 w-4 text-white" />
        </div>
      );
    } else if (action === "SIGNED_DOCUMENT" || action === "COMPLETED_SIGN_REQUEST" || action === "SENT_FINAL_DOCUMENT") {
      return (
        <div className={`${iconClass} bg-emerald-500`}>
          <CheckCircleIcon className="h-4 w-4 text-white" />
        </div>
      );
    } else {
      return (
        <div className={`${iconClass} bg-gray-500`}>
          <div className="h-1.5 w-1.5 rounded-full bg-white"></div>
        </div>
      );
    }
  };

  // Format action name from SNAKE_CASE/UPPERCASE to Title Case
  const formatActionName = (action) => {
    if (!action) return "Activity";
    // Convert SNAKE_CASE to Title Case
    return action
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getActionTitle = (log) => {
    // contactId is now an object with name and email
    const contactName = log.contactId?.name || "Recipient";
    
    if (log.action === "SENT_EMAIL") {
      return `Sent to ${contactName}`;
    } else if (log.action === "DOCUMENT_OPENED") {
      return `Opened by ${contactName}`;
    } else if (log.action === "SIGNED_DOCUMENT") {
      return `Signed by ${contactName}`;
    } else if (log.action === "CREATED_SIGN_REQUEST") {
      return "Sign request created";
    } else if (log.action === "COMPLETED_SIGN_REQUEST") {
      return "Sign request completed";
    } else if (log.action === "SENT_FINAL_DOCUMENT") {
      return "Final document sent";
    }
    // Fallback to message if available, otherwise format the action name
    return log.message || formatActionName(log.action) || "Activity";
  };

  const getRecipientEmail = (log) => {
    // contactId is now an object with name and email
    return log.contactId?.email || null;
  };

  const getStatusBadge = (action) => {
    if (action === "SENT_EMAIL" || action === "CREATED_SIGN_REQUEST") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
          <PaperAirplaneIcon className="h-3 w-3" />
          Sent
        </span>
      );
    } else if (action === "DOCUMENT_OPENED") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
          <EyeIcon className="h-3 w-3" />
          Opened
        </span>
      );
    } else if (action === "SIGNED_DOCUMENT") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
          <CheckCircleIcon className="h-3 w-3" />
          Signed
        </span>
      );
    } else if (action === "COMPLETED_SIGN_REQUEST" || action === "SENT_FINAL_DOCUMENT") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
          <CheckCircleIcon className="h-3 w-3" />
          Verified
        </span>
      );
    }
    return (
      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        {formatActionName(action) || "Activity"}
      </span>
    );
  };

  const getCardBackgroundColor = (action) => {
    if (action === "SENT_EMAIL" || action === "CREATED_SIGN_REQUEST") {
      return "bg-primary/5";
    } else if (action === "DOCUMENT_OPENED") {
      return "bg-amber-50";
    } else if (action === "SIGNED_DOCUMENT" || action === "COMPLETED_SIGN_REQUEST" || action === "SENT_FINAL_DOCUMENT") {
      return "bg-emerald-50";
    }
    return "bg-gray-50";
  };

  const formatLocation = (log) => {
    // Use location string if available (from API response)
    if (log.location) return log.location;
    
    // Fallback to locationDetails object if available
    if (log.locationDetails) {
      const parts = [];
      if (log.locationDetails.city) parts.push(log.locationDetails.city);
      if (log.locationDetails.state || log.locationDetails.region) {
        parts.push(log.locationDetails.state || log.locationDetails.region);
      }
      if (log.locationDetails.country) parts.push(log.locationDetails.country);
      if (log.locationDetails.countryCode && parts.length === 0) {
        // If only countryCode is available, use it
        parts.push(log.locationDetails.countryCode);
      }
      if (parts.length > 0) return parts.join(" / ");
    }
    
    return null; // Return null to conditionally render
  };

  const formatDevice = (log) => {
    // Use device directly from API response
    if (log.device && log.device.trim() !== "") return log.device;
    return null;
  };

  const formatBrowser = (log) => {
    // Use browser directly from API response
    if (log.browser && log.browser.trim() !== "") return log.browser;
    return null;
  };

  const formatIP = (log) => {
    // Return IP address if available, otherwise null
    if (log.ipAddress && log.ipAddress.trim() !== "" && log.ipAddress !== "Unknown") {
      return log.ipAddress;
    }
    return null;
  };

  if (!isOpen) return null;

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdrop
      zIndex="z-[60]"
      rootProps={{ "data-audit-trail-modal": true }}
      contentClassName="relative bg-white rounded-xl text-left overflow-hidden shadow-2xl sm:max-w-2xl sm:w-full"
    >
      {/* Header */}
      <div className="bg-white px-6 py-5 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                <ClockIcon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Audit Trail</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {documentName ? `${documentName} • ` : ""}Document activity
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
      </div>

      {/* Content */}
      <div className="bg-gray-50/50 px-6 py-6 max-h-[70vh] overflow-y-auto">
        {isLoading ? (
          <div className="space-y-4 animate-pulse">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex gap-4">
                <div className="w-8 h-8 bg-gray-200 rounded-full shrink-0" />
                <div className="flex-1">
                  <div className="h-4 w-40 bg-gray-200 rounded mb-2" />
                  <div className="h-3 w-24 bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ClockIcon className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium">No activity recorded yet</p>
            <p className="text-sm text-gray-400 mt-1">Activity will appear here as recipients interact with the document</p>
          </div>
        ) : (
              <div className="relative">
                {/* Expand/Collapse all */}
                {allLogIds.length > 0 && (
                  <div className="flex justify-end mb-4">
                    <button
                      onClick={expandCollapseAll}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors"
                    >
                      {allExpanded ? (
                        <>
                          <ChevronUpIcon className="h-4 w-4" />
                          Collapse all
                        </>
                      ) : (
                        <>
                          <ChevronDownIcon className="h-4 w-4" />
                          Expand all
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Timeline line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                {/* Timeline items */}
                <div className="space-y-4">
                  {auditLogs.map((log, index) => {
                    const logId = log._id || index;
                    const isExpanded = expandedLogs.has(logId);
                    const showDetails = hasDetails(log);
                    return (
                      <motion.div
                        key={logId}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05, duration: 0.3 }}
                        className="relative pl-11"
                      >
                        {/* Icon */}
                        <motion.div
                          className="absolute left-0 top-0 z-10"
                          whileHover={{ scale: 1.1 }}
                          transition={{ type: "spring", stiffness: 400 }}
                        >
                          {getActionIcon(log.action)}
                        </motion.div>

                        {/* Content Card */}
                        <motion.div
                          className={`group ${getCardBackgroundColor(log.action)} border rounded-xl p-4 cursor-default transition-all duration-200 ${
                            showDetails ? "cursor-pointer hover:border-gray-300 hover:shadow-md" : "border-gray-200 shadow-sm"
                          }`}
                          onClick={() => showDetails && toggleLogExpanded(logId)}
                          whileHover={showDetails ? { y: -2 } : {}}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <UserIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                <h4 className="text-sm font-semibold text-gray-900">
                                  {getActionTitle(log)}
                                </h4>
                              </div>
                              {getRecipientEmail(log) && (
                                <p className="text-xs text-gray-500 ml-6">
                                  {getRecipientEmail(log)}
                                </p>
                              )}
                            </div>
                            <div className="ml-4 flex items-center gap-2 flex-shrink-0">
                              {getStatusBadge(log.action)}
                              {showDetails && (
                                <span
                                  className="inline-flex items-center text-gray-400 group-hover:text-gray-600 transition-colors"
                                  title={isExpanded ? "Hide details" : "View details"}
                                >
                                  {isExpanded ? (
                                    <ChevronUpIcon className="h-4 w-4" />
                                  ) : (
                                    <ChevronDownIcon className="h-4 w-4" />
                                  )}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Details - Expandable */}
                          <AnimatePresence>
                            {isExpanded && showDetails && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="mt-4 pt-4 border-t border-gray-200/80 space-y-3 text-xs text-gray-600">
                                  {/* Check if this is a sent or signed activity */}
                                  {(() => {
                            const isSentOrSigned = 
                              log.action === "SENT_EMAIL" || 
                              log.action === "CREATED_SIGN_REQUEST" || 
                              log.action === "SIGNED_DOCUMENT";
                            
                            const device = formatDevice(log);
                            const browser = formatBrowser(log);
                            const ip = formatIP(log);
                            const location = isSentOrSigned ? formatLocation(log) : null;
                            
                            // Count available fields for dynamic grid
                            const firstRowFields = [
                              { key: "datetime", show: true },
                              { key: "device", show: device !== null },
                              { key: "browser", show: browser !== null }
                            ].filter(f => f.show);
                            
                            const secondRowFields = [
                              { key: "ip", show: ip !== null },
                              { key: "location", show: location !== null }
                            ].filter(f => f.show);
                            const secondRowColumns = Math.max(secondRowFields.length || 0, 3);
                            
                            return (
                              <>
                                {/* First Row: Date & Time, Device, Browser */}
                                {firstRowFields.length > 0 && (
                                  <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${firstRowFields.length}, 1fr)` }}>
                                    <div className="flex items-center gap-2">
                                      <ClockIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                      <div className="min-w-0">
                                        <span className="font-medium text-gray-700 block">Date & Time</span>
                                        <div className="text-gray-600">
                                          {formatForDisplay(
                                            log.createdAt || log.timestamp,
                                            "DD/MM/YYYY, hh:mm a"
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    {device !== null && (
                                      <div className="flex items-center gap-2">
                                        <ComputerDesktopIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                        <div className="min-w-0">
                                          <span className="font-medium text-gray-700 block">Device</span>
                                          <div className="text-gray-600">{device}</div>
                                        </div>
                                      </div>
                                    )}
                                    {browser !== null && (
                                      <div className="flex items-center gap-2">
                                        <GlobeAltIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                        <div className="min-w-0">
                                          <span className="font-medium text-gray-700 block">Browser</span>
                                          <div className="text-gray-600">{browser}</div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Second Row: IP, Location (only for sent/signed) */}
                                {secondRowFields.length > 0 && (
                                  <div
                                    className="grid gap-4"
                                    style={{ gridTemplateColumns: `repeat(${secondRowColumns}, minmax(0, 1fr))` }}
                                  >
                                    {ip !== null && (
                                      <div className="flex items-center gap-2">
                                        <MapPinIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                        <div className="min-w-0">
                                          <span className="font-medium text-gray-700 block">IP</span>
                                          <div className="text-gray-600 break-words">{ip}</div>
                                        </div>
                                      </div>
                                    )}
                                    {location !== null && (
                                      <div className="flex items-center gap-2">
                                        <MapPinIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                        <div className="min-w-0">
                                          <span className="font-medium text-gray-700 block">Location</span>
                                          <div className="text-gray-600 break-words">{location}</div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            );
                                  })()}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
    </AnimatedModal>
  );
};

export default DocumentAuditTrail;

